'use client';

import { alpha, Box, Button, Typography } from '@mui/material';
import { radius } from '@odys/design-system/tokens/radius';

import { Link } from '@/configurations/i18n/navigation';

import type { ReactNode } from 'react';

/**
 * Opacité du voile.
 *
 * Assez sombre pour que le texte blanc passe le contraste AA par-dessus une
 * photo claire, assez transparent pour qu'on devine encore la card qu'on
 * demande à l'utilisateur de débloquer.
 */
const SCRIM_OPACITY = 0.6;

export type VisitorOverlayProps = {
    /** Ce qu'on propose au visiteur, déjà traduit. */
    message: string;
    /** Libellé du bouton, déjà traduit. */
    ctaLabel: string;
    /** Produit `${prefix}-wrapper`, `${prefix}` et `${prefix}-cta`. */
    testIdPrefix: string;
    children: ReactNode;
};

/**
 * Voile d'invitation à l'inscription, posé sur une card pour un visiteur non
 * connecté.
 *
 * Il apparaît au survol **et au focus clavier** (`:focus-within` plus un
 * `tabIndex` sur le conteneur) : sans cela, l'invitation serait invisible à qui
 * navigue au clavier, c'est-à-dire précisément inaccessible à ceux qui ne
 * peuvent pas survoler.
 *
 * Le listing l'active ; la fiche DMC garde son propre CTA de page.
 */
export function VisitorOverlay({ message, ctaLabel, testIdPrefix, children }: VisitorOverlayProps) {
    return (
        <Box
            data-testid={`${testIdPrefix}-wrapper`}
            tabIndex={0}
            sx={{
                position: 'relative',
                cursor: 'default',
                '&:hover [data-overlay]': { opacity: 1, pointerEvents: 'auto' },
                '&:focus-within [data-overlay]': { opacity: 1, pointerEvents: 'auto' },
            }}
        >
            {children}
            <Box
                data-overlay
                data-testid={testIdPrefix}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: (theme) => alpha(theme.palette.common.black, SCRIM_OPACITY),
                    color: 'common.white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    px: 2,
                    textAlign: 'center',
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity .15s ease',
                    borderRadius: radius.lg,
                }}
            >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {message}
                </Typography>
                <Button
                    component={Link}
                    href="/sign-up"
                    variant="contained"
                    color="primary"
                    size="small"
                    data-testid={`${testIdPrefix}-cta`}
                >
                    {ctaLabel}
                </Button>
            </Box>
        </Box>
    );
}
